import React, { useEffect, useState } from "react";
import api from "../../../api/axios";

/* ── Store scope picker — shared by every master form ──────────────
   value    { availableInAllStores, stores, excludedStores }
   onChange next value of the same shape
   compact  tighter margins for dense forms

   Renders NOTHING while the estate has fewer than two stores, so a
   single-store setup never sees multi-store UI. The store list is
   fetched once per portal session and shared between all forms.    */

let storeCache = null;
let storePromise = null;
const loadStores = () => {
  if (storeCache) return Promise.resolve(storeCache);
  if (!storePromise) {
    storePromise = api
      /* the payload IS the array — {id, code, name, isDefault, isActive, …} */
      .get("/admin/stores")
      .then((res) => {
        storeCache = Array.isArray(res.data) ? res.data : [];
        return storeCache;
      })
      .catch(() => {
        /* failed fetch stays retryable on the next mount */
        storePromise = null;
        return [];
      });
  }
  return storePromise;
};

export const defaultStoreScope = () => ({
  availableInAllStores: true,
  stores: [],
  excludedStores: [],
});

/* the service sends stores/excludedStores as plain string ids; the object
   branch is left in as a harmless guard for anything still populated */
export const storeScopeOf = (item) => ({
  availableInAllStores: item?.availableInAllStores !== false,
  stores: (item?.stores || []).map((s) => String(s?._id || s)),
  excludedStores: (item?.excludedStores || []).map((s) => String(s?._id || s)),
});

const LABEL = {
  display: "block",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 1,
  color: "var(--mly-text-muted)",
  marginBottom: 8,
  textTransform: "uppercase",
};

const BOX = {
  border: "1.5px solid var(--mly-border)",
  borderRadius: 10,
  background: "var(--mly-surface)",
  padding: "8px 12px",
  maxHeight: 180,
  overflowY: "auto",
};

const StoreScopePicker = ({ value, onChange, compact }) => {
  const [stores, setStores] = useState(storeCache || []);
  const [showExclusions, setShowExclusions] = useState(
    (value?.excludedStores || []).length > 0
  );

  useEffect(() => {
    let alive = true;
    loadStores().then((list) => {
      if (alive) setStores(list);
    });
    return () => {
      alive = false;
    };
  }, []);

  const scope = {
    availableInAllStores: value?.availableInAllStores !== false,
    stores: value?.stores || [],
    excludedStores: value?.excludedStores || [],
  };

  /* an item that already hides in some stores keeps its section open */
  useEffect(() => {
    if (scope.excludedStores.length > 0) setShowExclusions(true);
  }, [scope.excludedStores.length]);

  /* single-store estates never see this UI */
  if (stores.length < 2) return null;

  const set = (patch) => onChange({ ...scope, ...patch });
  const toggleIn = (list, id) =>
    list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

  const storeRow = (store, checked, onToggle, color) => (
    <div
      key={store.id}
      onClick={onToggle}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "6px 2px",
        cursor: "pointer",
        userSelect: "none",
      }}
    >
      <div
        style={{
          width: 18,
          height: 18,
          borderRadius: 5,
          border: `2px solid ${checked ? color : "var(--mly-border)"}`,
          background: checked ? color : "var(--mly-surface)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          transition: "all 0.15s",
        }}
      >
        {checked && (
          <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
            <polyline
              points="2,6 5,9 10,3"
              stroke="var(--mly-on-accent)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </div>
      <span
        style={{
          fontSize: 13,
          color: "var(--mly-text-soft)",
          fontWeight: checked ? 600 : 400,
        }}
      >
        {store.name}
        {store.code ? (
          <span style={{ color: "var(--mly-text-muted)", fontWeight: 400 }}> · {store.code}</span>
        ) : null}
        {store.isDefault ? (
          <span
            style={{
              marginLeft: 6,
              background: "var(--mly-accent-soft)",
              color: "var(--mly-heading)",
              fontSize: 9,
              fontWeight: 700,
              padding: "1px 7px",
              borderRadius: 20,
              border: "1px solid var(--mly-border)",
            }}
          >
            MAIN
          </span>
        ) : null}
      </span>
    </div>
  );

  return (
    <div style={{ marginBottom: compact ? 12 : 20 }}>
      <label style={LABEL}>
        Store Availability
        <span
          style={{
            fontWeight: 500,
            letterSpacing: 0,
            textTransform: "none",
            color: "var(--mly-text-muted)",
            marginLeft: 6,
          }}
        >
          (which stores show this)
        </span>
      </label>

      {/* all-stores / restricted — a radio pair styled as two pills */}
      <div style={{ display: "flex", gap: 10 }}>
        {[
          { all: true, label: "All stores" },
          { all: false, label: "Only selected stores" },
        ].map(({ all, label }) => {
          const sel = scope.availableInAllStores === all;
          return (
            <div
              key={label}
              onClick={() =>
                all
                  ? set({ availableInAllStores: true, stores: [] })
                  : set({ availableInAllStores: false, excludedStores: [] })
              }
              style={{
                flex: 1,
                padding: "9px 0",
                borderRadius: 10,
                textAlign: "center",
                cursor: "pointer",
                fontSize: 13,
                fontWeight: 700,
                userSelect: "none",
                border: sel
                  ? "2px solid var(--mly-heading)"
                  : "1.5px solid var(--mly-border)",
                background: sel ? "var(--mly-accent-soft)" : "var(--mly-surface-2)",
                color: sel ? "var(--mly-heading)" : "var(--mly-text-muted)",
                transition: "all 0.15s",
              }}
            >
              {label}
            </div>
          );
        })}
      </div>

      {/* restricted: pick the whitelist */}
      {!scope.availableInAllStores && (
        <div style={{ marginTop: 10, ...BOX }}>
          {stores.map((s) =>
            storeRow(
              s,
              scope.stores.includes(String(s.id)),
              () => set({ stores: toggleIn(scope.stores, String(s.id)) }),
              "var(--mly-heading)"
            )
          )}
          {scope.stores.length === 0 && (
            <div style={{ fontSize: 11, color: "#ef4444", padding: "4px 2px" }}>
              Select at least one store.
            </div>
          )}
        </div>
      )}

      {/* all-stores: optional subtractive exclusions, collapsed by default */}
      {scope.availableInAllStores && (
        <div style={{ marginTop: 8 }}>
          <span
            onClick={() => setShowExclusions((v) => !v)}
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: "var(--mly-heading)",
              cursor: "pointer",
              userSelect: "none",
            }}
          >
            {showExclusions ? "▾" : "▸"} Hide in specific stores
            {scope.excludedStores.length > 0
              ? ` (${scope.excludedStores.length})`
              : ""}
          </span>
          {showExclusions && (
            <div style={{ marginTop: 8, ...BOX }}>
              {stores.map((s) =>
                storeRow(
                  s,
                  scope.excludedStores.includes(String(s.id)),
                  () =>
                    set({
                      excludedStores: toggleIn(
                        scope.excludedStores,
                        String(s.id)
                      ),
                    }),
                  "#b45309"
                )
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StoreScopePicker;
